import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  PartialState,
  Select,
  StatusBadge,
} from "@evolua/ui";
import { INTEGRATION_PROVIDERS } from "@evolua/schemas";
import { useState, type FormEvent } from "react";
import {
  useCreateIntegration,
  useDisableIntegration,
  useIntegrations,
  useUpdateIntegration,
} from "../api/use-integrations.js";
import { hasPermission } from "../session/demo-session.js";
import { useSession } from "../session/SessionContext.js";

export function IntegrationsPage() {
  const { session, isDemoMode } = useSession();
  const list = useIntegrations();
  const create = useCreateIntegration();
  const update = useUpdateIntegration();
  const disable = useDisableIntegration();
  const canManage = session ? hasPermission(session, ["integration:manage"]) : false;

  const [provider, setProvider] =
    useState<(typeof INTEGRATION_PROVIDERS)[number]>("TOTVS_RM");
  const [baseUrl, setBaseUrl] = useState("https://rm.demo.local/api");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [disableId, setDisableId] = useState<string | null>(null);

  if (!session) return null;

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!session) return;
    setFormError(null);
    try {
      await create.mutateAsync({
        provider,
        ...(baseUrl.trim() ? { baseUrl: baseUrl.trim() } : {}),
        companyId: session.companyId,
        credentials: {
          ...(clientId.trim() ? { clientId: clientId.trim() } : {}),
          ...(clientSecret.trim()
            ? { clientSecret: clientSecret.trim() }
            : { apiKey: "demo-key" }),
        },
      });
      setClientId("");
      setClientSecret("");
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Falha ao criar");
    }
  }

  const rows = list.data?.data ?? [];

  return (
    <>
      <PageHeader
        title="Integrações"
        lead={
          <>
            Conexão ERP por empresa. Credenciais vão ao Secrets Manager; o banco guarda só{" "}
            <code>secret_ref</code>.
          </>
        }
      />

      {isDemoMode ? (
        <PartialState description="Demo: store em sessionStorage (sem segredo). API real usa POST /v1/integrations + auditoria." />
      ) : null}

      {canManage ? (
        <Card title="Nova integração" className="evolua-stack evolua-stack--3">
          <form
            onSubmit={(e) => void onCreate(e)}
            className="evolua-stack evolua-stack--3"
          >
            <Select
              label="Provider"
              value={provider}
              onChange={(e) =>
                setProvider(e.target.value as (typeof INTEGRATION_PROVIDERS)[number])
              }
              options={INTEGRATION_PROVIDERS.map((p) => ({ value: p, label: p }))}
            />
            <Field
              label="URL base"
              name="baseUrl"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
            <Field
              label="Client ID"
              name="clientId"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              autoComplete="off"
            />
            <Field
              label="Client secret"
              name="clientSecret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              autoComplete="new-password"
            />
            {formError ? <Alert tone="danger">{formError}</Alert> : null}
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Salvando…" : "Criar"}
            </Button>
          </form>
        </Card>
      ) : (
        <PartialState description="Você pode consultar integrações; alterações exigem integration:manage." />
      )}

      {list.isPending ? <p className="evolua-page-lead">Carregando…</p> : null}
      {!list.isPending && rows.length === 0 ? (
        <EmptyState
          title="Nenhuma integração"
          description="Cadastre o primeiro conector ERP para este tenant."
        />
      ) : null}

      <div className="evolua-stack evolua-stack--3">
        {rows.map((row) => (
          <Card key={row.id} title={row.provider}>
            <p>
              <StatusBadge status={row.status} /> · empresa {row.companyId ?? "—"}
            </p>
            <p>
              URL: {row.baseUrl ?? "—"}
              <br />
              secret_ref: <code>{row.secretRef ?? "—"}</code>
              <br />
              segredo: {row.hasSecret ? "configurado" : "ausente"}
            </p>
            {canManage && row.status !== "DISABLED" ? (
              <div className="evolua-stack evolua-stack--3">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={update.isPending}
                  onClick={() =>
                    void update.mutateAsync({
                      id: row.id,
                      body: {
                        baseUrl: `${row.baseUrl ?? "https://rm.demo.local"}/v2`,
                      },
                    })
                  }
                >
                  Atualizar URL
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="danger"
                  disabled={disable.isPending}
                  onClick={() => setDisableId(row.id)}
                >
                  Desativar
                </Button>
              </div>
            ) : null}
          </Card>
        ))}
      </div>

      <Modal
        open={Boolean(disableId)}
        title="Desativar integração"
        onClose={() => setDisableId(null)}
        size="sm"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setDisableId(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={disable.isPending}
              onClick={() => {
                if (!disableId) return;
                const id = disableId;
                setDisableId(null);
                void disable.mutateAsync(id);
              }}
            >
              Desativar
            </Button>
          </>
        }
      >
        <p>A integração deixa de sincronizar. Confirma desativar?</p>
      </Modal>
    </>
  );
}
